import { Controller } from "@hotwired/stimulus";

// Drives "Build your own kit" on the Referrals dashboard: posts the
// affiliate's inputs, polls the generation until the background job finishes,
// then swaps the server-rendered asset cards into the results target
// (Stimulus binds the injected clipboard-copy buttons automatically).
export default class extends Controller {
  static targets = [
    "form",
    "submit",
    "status",
    "results",
    "refine",
    "instruction",
  ];
  static values = {
    createUrl: String,
    programType: String,
  };

  static POLL_INTERVAL_MS = 2500;
  static MAX_POLLS = 40; // ~100s, past which we give up and unlock the form

  disconnect() {
    this._stopPolling();
  }

  generate(event) {
    event.preventDefault();
    this._submit();
  }

  // "Want changes?" — resubmits the same inputs plus a free-text revision
  // instruction; the server feeds the previous kit back to the model with it.
  revise(event) {
    event.preventDefault();
    const instruction = this.hasInstructionTarget
      ? this.instructionTarget.value.trim()
      : "";
    if (!instruction) return;
    this._submit(instruction);
  }

  async _submit(instruction) {
    if (this.busy) return;

    this._setBusy(true);
    this._showStatus(
      instruction
        ? "Rewriting your kit with your changes, usually under a minute…"
        : "Writing your kit, usually under a minute…"
    );

    try {
      const body = Object.fromEntries(new FormData(this.formTarget));
      body.program_type = this.programTypeValue;
      if (instruction) body.instruction = instruction;

      const resp = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok)
        throw new Error(
          data.error || "Something went wrong. Try again in a minute."
        );

      this._poll(data.id, this.constructor.MAX_POLLS);
    } catch (error) {
      this._fail(error.message);
    }
  }

  _poll(id, attemptsLeft) {
    if (attemptsLeft <= 0) {
      this._fail(
        "This is taking longer than expected. Reload the page in a minute; your kit may still arrive."
      );
      return;
    }

    this.pollTimer = setTimeout(async () => {
      try {
        const resp = await fetch(`${this.createUrlValue}/${id}`, {
          headers: { Accept: "application/json" },
        });
        if (!resp.ok)
          throw new Error("Something went wrong checking on your kit.");
        const data = await resp.json();

        if (data.status === "completed") {
          this.resultsTarget.innerHTML = data.html;
          this._hideStatus();
          this._setBusy(false);
          this._relabelSubmit();
          if (this.hasRefineTarget) this.refineTarget.hidden = false;
          if (this.hasInstructionTarget) this.instructionTarget.value = "";
        } else if (data.status === "failed") {
          this._fail(
            data.error_message || "We couldn't generate your kit this time."
          );
        } else {
          this._poll(id, attemptsLeft - 1);
        }
      } catch (error) {
        this._fail(error.message);
      }
    }, this.constructor.POLL_INTERVAL_MS);
  }

  _fail(message) {
    this._showStatus(message, true);
    this._setBusy(false);
  }

  _setBusy(busy) {
    this.busy = busy;
    if (this.hasSubmitTarget) {
      this.submitTarget.classList.toggle("loading", busy);
      this.submitTarget.disabled = busy;
    }
  }

  _showStatus(message, isError = false) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = false;
    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("generator-status--error", isError);
  }

  _hideStatus() {
    if (this.hasStatusTarget) this.statusTarget.hidden = true;
  }

  _relabelSubmit() {
    if (!this.hasSubmitTarget) return;
    const icon = this.submitTarget.querySelector("i.icon");
    this.submitTarget.textContent = " Regenerate my kit";
    if (icon) this.submitTarget.prepend(icon);
  }

  _stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
